import React, { createContext, useContext } from 'react';
import type { FC, ReactNode } from 'react';
import { match } from 'variant';
import { useTranslation } from '@wix/cli-i18n';
import { Spinner, useAsync, Alert } from '@wix/cli-ui-kit';
import { ErrorViewer, useErrorReporter } from '@wix/cli-error-reporting';
import { useTunnel } from '@wix/cli-tunnel';
import { useProjectModel } from '../../model.js';
import { useArmUpdater } from './useArmUpdater.js';
import type { BackendUrls } from './useArmUpdater.js';
import { useClientDevServer, useBackendDevServer } from './useDevServer.js';
import { ArmTagProvider } from '../../context/armTag.js';
import { useSiteOverrideUpdater } from './useSiteOverrideUpdater.js';
import { useBackendComponents } from '../../backend/useBackendComponents.js';
import { SiteOverrideProvider } from '../../context/siteOverride.js';
import type { Logger } from '../../vite/logger.js';
import { BlocksCLIDevServerProvider } from './BlocksCLIServerProvider.js';
import { BlocksTypesUpdater } from '../../blocks/BlocksTypesUpdater.js';

interface DevEnvironment {
  https: boolean;
  logger: Logger;
  clientDevServerUrl: string;
  backendDevServerUrl?: string;
}

const DevEnvironmentContext = createContext<DevEnvironment | null>(null);

export const DevEnvironmentProvider: FC<{
  https: boolean;
  logger: Logger;
  children: ReactNode;
  origin?: string;
  port?: number;
}> = ({ https, logger, origin, port, children }) => {
  const { t } = useTranslation();
  const { reportError } = useErrorReporter();
  const { backendComponents, backendComponentsByType } = useBackendComponents();
  const createClientDevServer = useClientDevServer({ https, port, origin });
  const createBackendDevServer = useBackendDevServer();
  const startTunnel = useTunnel();
  const createArmUpdater = useArmUpdater();
  const createSiteOverrideUpdater = useSiteOverrideUpdater();
  const { watcher: projectModelWatcher } = useProjectModel();

  const { status } = useAsync(async () => {
    let backendDevServerUrl: BackendUrls | undefined;

    if (backendComponents.length > 0) {
      backendDevServerUrl = await createBackendDevServer({
        projectModelWatcher,
        customLogger: logger,
      }).then(async ({ devServerUrl }) => {
        let tunnelUrl: string | undefined;
        if (
          backendComponentsByType.Event.length > 0 ||
          backendComponentsByType.ServicePlugin.length > 0
        ) {
          const { url } = await startTunnel(Number(new URL(devServerUrl).port));
          tunnelUrl = url;
        }
        return { localUrl: devServerUrl, tunnelUrl };
      });
    }

    const { devServerUrl: clientDevServerUrl, devServer: clientDevServer } =
      await createClientDevServer({
        baseBackendUrl: backendDevServerUrl?.localUrl,
        projectModelWatcher,
        customLogger: logger,
      });

    const armTag = await createArmUpdater({
      backendServerUrl: backendDevServerUrl,
      devServerUrl: clientDevServerUrl,
      projectModelWatcher,
      onChange(changeType) {
        match(changeType, {
          'component-updated': () => {
            logger.info(t('dev_command.component_updated'));
          },
          'change-received': () => {
            logger.info(t('dev_command.config_change'));
          },
          'model-did-not-change': () => {
            logger.info(t('dev_command.config_didnt_update'));
          },
          'model-updated': () => {
            clientDevServer.ws.send({ type: 'full-reload' });
            logger.info(t('dev_command.config_update'));
          },
          'blocks-revision-changed': () => {
            logger.info(t('dev_command.blocks_revision_updated'));
          },
        });
      },
      onError(error) {
        reportError(error);

        logger.error(<ErrorViewer error={error} />);
        logger.info(t('dev_command.config_didnt_update'));
      },
    });

    const siteOverrideId = await createSiteOverrideUpdater({
      projectModelWatcher,
    });

    logger.info(
      <Alert type="success">{t('dev_command.dev_environment.started')}</Alert>
    );

    return {
      clientDevServer,
      clientDevServerUrl,
      armTag,
      siteOverrideId,
      https,
    };
  }, []);

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text={t('dev_command.dev_environment.loading')} />,
    Success: ({
      result: {
        clientDevServer,
        clientDevServerUrl,
        https,
        armTag,
        siteOverrideId,
      },
    }) => (
      <DevEnvironmentContext.Provider
        value={{
          clientDevServerUrl,
          https,
          logger,
        }}
      >
        <BlocksTypesUpdater logger={logger}>
          <BlocksCLIDevServerProvider httpServer={clientDevServer.httpServer}>
            <SiteOverrideProvider siteOverrideId={siteOverrideId}>
              <ArmTagProvider armTag={armTag}>{children}</ArmTagProvider>
            </SiteOverrideProvider>
          </BlocksCLIDevServerProvider>
        </BlocksTypesUpdater>
      </DevEnvironmentContext.Provider>
    ),
  });
};

export function useDevEnvironment(): DevEnvironment {
  const context = useContext(DevEnvironmentContext);
  if (context == null) {
    throw new Error(
      'useDevEnvironment() cannot be used without an <DevEnvironmentProvider>'
    );
  }
  return context;
}
